// Asynchronous half of the client-side reset. The synchronous half (localStorage,
// sessionStorage, cookies) lives in bootClean.js, which must run before AppKit is
// constructed; this file finishes the job once the module graph has loaded.
//
// Covered here and NOT by a browser hard-reload (Ctrl+Shift+R), which is why a
// hard-reload never fixed a wedged partner:
//   - IndexedDB  — WalletConnect's session store
//   - Cache API  — app shell / JS bundle
//   - service workers — legacy cache-first workers serving a stale shell

import { clearCookies, clearWalletLocalStorage } from "./bootClean";

const WALLET_DB = /wallet|walletconnect|wc@|w3m|reown|appkit|keyvaluestorage/i;

const deleteDatabase = (name) =>
    new Promise((resolve) => {
        try {
            const req = window.indexedDB.deleteDatabase(name);
            req.onsuccess = req.onerror = req.onblocked = () => resolve(name);
        } catch (e) {
            resolve(null);
        }
    });

/** Drop IndexedDB databases — all of them, or only the wallet ones. */
const clearIndexedDb = async (walletOnly) => {
    try {
        if (!window.indexedDB || typeof window.indexedDB.databases !== "function") return;
        const dbs = await window.indexedDB.databases();
        const names = dbs
            .map((db) => db.name)
            .filter(Boolean)
            .filter((name) => (walletOnly ? WALLET_DB.test(name) : true));
        const done = await Promise.all(names.map(deleteDatabase));
        if (done.filter(Boolean).length) {
            console.log("[cacheReset] Cleared IndexedDB:", done.filter(Boolean));
        }
    } catch (e) {
        console.warn("[cacheReset] IndexedDB clear skipped:", e && e.message);
    }
};

/** Drop the Cache API entries (app shell / JS bundle). */
const clearCacheApi = async () => {
    try {
        if (!("caches" in window)) return;
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
        if (names.length) console.log("[cacheReset] Cleared Cache API:", names);
    } catch (e) {
        console.warn("[cacheReset] Cache API clear skipped:", e && e.message);
    }
};

/** Unregister every service worker. */
const unregisterServiceWorkers = async () => {
    try {
        if (!("serviceWorker" in navigator)) return;
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
    } catch (e) {
        console.warn("[cacheReset] SW unregister skipped:", e && e.message);
    }
};

/**
 * Finish a reset that bootClean already started synchronously.
 * `walletOnly` keeps the app shell cache and the service workers alone — the
 * everyday logged-out cleanup has no reason to re-download the bundle.
 */
export const purgeBrowserStores = async ({ walletOnly = false } = {}) => {
    if (walletOnly) {
        await clearIndexedDb(true);
        return;
    }
    await Promise.all([clearIndexedDb(false), clearCacheApi(), unregisterServiceWorkers()]);
};

/** Cache-busting navigation to the current page, minus any ?reset flag. */
const reloadClean = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("reset");
    url.searchParams.set("_cb", Date.now().toString(36));
    window.location.replace(url.toString());
};

/**
 * The nuke, callable at any time (support link, a button in the UI).
 * Everything the browser holds for this origin, then a clean reload.
 */
export const hardReset = async ({ reload = true } = {}) => {
    console.log("[cacheReset] Hard reset starting");
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch (e) {
        console.warn("[cacheReset] Storage clear skipped:", e && e.message);
    }
    clearCookies();
    clearWalletLocalStorage();
    await purgeBrowserStores({ walletOnly: false });
    try {
        sessionStorage.setItem("manualLogout", "true");
    } catch (e) {
        /* ignore */
    }
    console.log("[cacheReset] Hard reset done");
    if (reload) reloadClean();
};

/**
 * Detect that this browser is RUNNING A STALE BUNDLE.
 *
 * The old version check compared localStorage against the constant compiled
 * INTO the bundle, so a browser serving yesterday's JS compared an old value
 * with an old value and concluded all was well. /version.json is written at
 * build time and fetched with `cache: no-store`, so it reports what is actually
 * DEPLOYED. Note this is belt-and-braces: the wedged-partner case turned out to
 * be stale storage at the same version, which bootClean now handles.
 */
export const checkForNewVersion = async (runningVersion) => {
    try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const { version: deployedVersion } = await res.json();
        if (!deployedVersion || deployedVersion === runningVersion) {
            sessionStorage.removeItem("staleBundleReloaded");
            return;
        }
        if (sessionStorage.getItem("staleBundleReloaded") === deployedVersion) {
            console.warn(
                `[cacheReset] Still running ${runningVersion} after a reload; deployed is ${deployedVersion}.`
            );
            return;
        }
        console.warn(
            `[cacheReset] Stale bundle: running ${runningVersion}, deployed ${deployedVersion}. Resetting.`
        );
        await hardReset({ reload: false });
        try {
            sessionStorage.setItem("staleBundleReloaded", deployedVersion);
        } catch (e) {
            /* ignore */
        }
        reloadClean();
    } catch (e) {
        // Offline, or version.json missing — never block boot over this.
        console.warn("[cacheReset] Version check skipped:", e && e.message);
    }
};
